import {
  Duration,
  Interval,
  Locale,
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  addYears,
  areIntervalsOverlapping,
  differenceInCalendarDays,
  differenceInDays,
  differenceInSeconds,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  formatDuration,
  hoursToSeconds,
  intervalToDuration,
  isAfter,
  isBefore,
  isSameDay,
  isSameHour,
  isSameMinute,
  isSameSecond,
  isSameWeek,
  isToday,
  isValid,
  isWeekend,
  isWithinInterval,
  minutesToSeconds,
  parse,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYear,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { daysInWeek, secondsInHour, secondsInMinute } from 'date-fns/constants';
import { FormatDurationOptions } from 'date-fns/formatDuration';
import moment from 'moment-timezone';
import { assert } from '../../../core/assert/assert';
import { DefaultDateFormat } from '../../../shared/localization/formats';
import { Epoch, WeekdayOrdinal } from '../calendar-types';

export class DateTimeHelper {
  public static parse(
    dateString: string,
    formatString: string,
    referenceDate: Date | number,
    options?: unknown,
  ): Date {
    return parse(dateString, formatString, referenceDate, options);
  }

  public static parseISO(argument: string, options?: { additionalDigits?: 0 | 1 | 2 }): Date {
    return parseISO(argument, options);
  }

  public static isValid(date: unknown): boolean {
    return isValid(date);
  }

  public static format(date: Date | number, pattern = DefaultDateFormat): string {
    return format(date, pattern);
  }

  public static formatDuration(duration: Duration, options?: FormatDurationOptions): string {
    return formatDuration(duration, options);
  }

  public static addHours(date: Date | number, amount: number): Date {
    return addHours(date, amount);
  }

  public static addMinutes(date: Date | number, amount: number): Date {
    return addMinutes(date, amount);
  }

  public static addMonths(date: Date | number, amount: number): Date {
    return addMonths(date, amount);
  }

  public static addWeeks(date: Date | number, amount: number): Date {
    return addWeeks(date, amount);
  }

  public static addDays(date: Date | number, amount: number): Date {
    return addDays(date, amount);
  }

  public static addYears(date: Date | number, amount: number): Date {
    return addYears(date, amount);
  }

  public static addSeconds(date: Date | number, amount: number): Date {
    return addSeconds(date, amount);
  }

  public static areIntervalsOverlapping(
    intervalLeft: Interval,
    intervalRight: Interval,
    options?: {
      inclusive?: boolean;
    },
  ): boolean {
    return areIntervalsOverlapping(intervalLeft, intervalRight, options);
  }

  public static subMinutes(date: Date | number, amount: number): Date {
    return subMinutes(date, amount);
  }

  public static differenceInHours(start: Date | number, end: Date | number): number {
    const differenceInSeconds = DateTimeHelper.differenceInSeconds(start, end);
    return DateTimeHelper.secondsToHours(differenceInSeconds);
  }

  public static differenceInMinutes(start: Date | number, end: Date | number): number {
    const momEnd = moment(end);
    const duration = moment.duration(momEnd.diff(start));
    return duration.asMinutes();
  }

  public static differenceInDays(dateLeft: Date | number, dateRight: Date | number): number {
    return differenceInDays(dateLeft, dateRight);
  }

  public static differenceInSeconds(dateLeft: Date | number, dateRight: Date | number): number {
    return differenceInSeconds(dateLeft, dateRight);
  }

  public static differenceInCalendarDays(
    dateLeft: Date | number,
    dateRight: Date | number,
  ): number {
    return differenceInCalendarDays(dateLeft, dateRight);
  }

  public static intervalToDuration(interval: Interval): Duration {
    return intervalToDuration(interval);
  }

  public static isBefore(date: Date | number, compare: Date | number): boolean {
    return isBefore(date, compare);
  }

  public static isAfter(date: Date | number, compare: Date | number): boolean {
    return isAfter(date, compare);
  }

  public static isToday(date: Date | number): boolean {
    return isToday(date);
  }

  public static isSameWeek(
    dateLeft: Date | number,
    dateRight: Date | number,
    options?: {
      locale?: Locale;
      weekStartsOn?: WeekdayOrdinal;
    },
  ): boolean {
    return isSameWeek(dateLeft, dateRight, options);
  }

  public static isSameDay(dateLeft: Date | number, dateRight: Date | number): boolean {
    return isSameDay(dateLeft, dateRight);
  }

  /**
   * Only works if the date part is the same of both dates.
   */
  public static isSameTime(dateLeft: Date, dateRight: Date): boolean {
    return (
      isSameHour(dateLeft, dateRight) &&
      isSameMinute(dateLeft, dateRight) &&
      isSameSecond(dateLeft, dateRight)
    );
  }

  public static isWithinInterval(
    date: Date | number,
    interval: Interval,
    startIncluded = true,
    endIncluded = true,
  ): boolean {
    const start = new Date(interval.start);
    const end = new Date(interval.end);

    if (startIncluded && endIncluded) {
      return isWithinInterval(date, interval);
    } else if (startIncluded && !endIncluded) {
      return isWithinInterval(date, interval) && DateTimeHelper.isBefore(date, end);
    } else if (!startIncluded && endIncluded) {
      return isWithinInterval(date, interval) && DateTimeHelper.isAfter(date, start);
    }
    // !startIncluded && !endIncluded
    return (
      isWithinInterval(date, interval) &&
      DateTimeHelper.isBefore(date, end) &&
      DateTimeHelper.isAfter(date, start)
    );
  }

  /**
   * Checks if `date` falls between `start` and `end`, where start or end can be null.
   * - If `start` is null, we only check that `date < end`.
   * - If `end` is null, we only check that `date > start`.
   * - If both are given, we ensure `start < date < end` (exclusive).
   * - If both are null, returns `true` by default (allow all).
   * @returns true if `date` meets the criteria
   */
  public static isDateInOpenInterval(date: Date, interval: Interval): boolean {
    // Case 1: date >= start && date <= end
    if (interval.start && interval.end) {
      return !isBefore(date, interval.start) && !isAfter(date, interval.end);
    }

    // Case 2: No start, only end — date <= end
    if (!interval.start && interval.end) {
      return !isAfter(date, interval.end);
    }

    // Case 3: No end, only start — date >= start
    if (interval.start && !interval.end) {
      return !isBefore(date, interval.start);
    }

    // Case 4: Both start and end are null — decide your default
    return true;
  }

  public static isMidnight(endTime: Date): boolean {
    return DateTimeHelper.isSameTime(endTime, DateTimeHelper.startOfDay(endTime));
  }

  public static startOfToday(): Date {
    return startOfToday();
  }

  public static startOfDay(date: Date): Date {
    return startOfDay(date);
  }

  public static startOfWeek(
    date: Date | number,
    options?: { locale?: Locale; weekStartsOn?: WeekdayOrdinal },
  ): Date {
    return startOfWeek(date, options);
  }

  public static startOfNextDay(date: Date): Date {
    return DateTimeHelper.addDays(DateTimeHelper.startOfDay(date), 1);
  }

  public static endOfDay(date: Date): Date {
    return endOfDay(date);
  }

  public static endOfWeek(
    date: Date | number,
    options?: { locale?: Locale; weekStartsOn?: WeekdayOrdinal },
  ): Date {
    return endOfWeek(date, options);
  }

  public static daysInWeek(): number {
    return daysInWeek;
  }

  public static startOfMonth(date: Date): Date {
    return startOfMonth(date);
  }

  public static endOfMonth(date: Date): Date {
    return endOfMonth(date);
  }

  public static startOfYear(date: Date): Date {
    return startOfYear(date);
  }

  public static endOfYear(date: Date): Date {
    return endOfYear(date);
  }

  public static subMonths(date: Date | number, amount: number): Date {
    return subMonths(date, amount);
  }

  public static subWeeks(date: Date | number, amount: number): Date {
    return subWeeks(date, amount);
  }

  public static subDays(date: Date | number, amount: number): Date {
    return subDays(date, amount);
  }

  public static subHours(date: Date | number, amount: number): Date {
    return subHours(date, amount);
  }

  public static subYears(date: Date | number, amount: number): Date {
    return subYears(date, amount);
  }

  public static isWeekend(date: Date | number | string): boolean {
    return isWeekend(date);
  }

  public static eachDayOfInterval(start: Date, end: Date, checkStartBeforeEnd = true): Date[] {
    assert(
      !checkStartBeforeEnd || !DateTimeHelper.isBefore(end, start),
      'End date is before start date.',
    );
    return eachDayOfInterval({ start, end });
  }

  public static eachWeekOfInterval(
    start: Date,
    end: Date,
    options?: { locale?: Locale; weekStartsOn?: WeekdayOrdinal },
  ): Date[] {
    return eachWeekOfInterval({ start, end }, options);
  }

  public static weekNumber(date?: Date): number {
    if (!date) {
      return moment().isoWeek();
    }
    return moment(date).isoWeek();
  }

  /**
   * Return the year of the calendar week of this date. For example, for 02.01.2023, this method returns 2023.
   * For 01.01.2006, this method returns 2005.
   */
  public static yearNumberOfCalendarWeek(date: Date, weekStartsOn?: WeekdayOrdinal): number {
    const weekNumber = DateTimeHelper.weekNumber(date);
    // take last day of the first calendar week to make sure its the current year
    if (weekNumber === 1) {
      return moment(DateTimeHelper.endOfWeek(date, { weekStartsOn })).year();
    }
    if (weekNumber === 52 || weekNumber === 53) {
      return moment(DateTimeHelper.startOfWeek(date, { weekStartsOn })).year();
    }
    return moment(date).year();
  }

  public static isWhen(day: Date): Epoch {
    const today = new Date();
    today.setHours(0, 0, 0);

    if (DateTimeHelper.isToday(day)) {
      return 'today';
    } else if (DateTimeHelper.isAfter(day, today)) {
      return 'future';
    } else if (DateTimeHelper.isBefore(day, today)) {
      return 'past';
    }
    return 'unknown';
  }

  public static mergeDateAndTime(dateVal: Date, timeVal: Date): Date {
    return new Date(
      dateVal.getFullYear(),
      dateVal.getMonth(),
      dateVal.getDate(),
      timeVal.getHours(),
      timeVal.getMinutes(),
      timeVal.getSeconds(),
      0,
    );
  }

  public static timeStringToDate(timeString: string): Date {
    // Get the current date
    const newDate = new Date();

    // Create a new date object with the time set to "08:00"
    const timeParts = timeString.split(':');
    newDate.setHours(+timeParts[0]);
    newDate.setMinutes(+timeParts[1]);
    newDate.setSeconds(+timeParts[2]);
    newDate.setMilliseconds(0);
    assert(!isNaN(newDate.getTime()));
    return newDate;
  }

  /**
   * @param date If date has a time between 23:59:01 (inclusive) and 23:59:59 (inclusive), transform it to 00:00:00.
   * @param useNextDay Whether to use the next day or keep the current day.
   */
  public static transformToExactlyMidnightIfNeeded(date: Date, useNextDay = false): Date {
    const right = new Date(date);
    right.setHours(23, 59, 0, 0);

    if (DateTimeHelper.differenceInSeconds(date, right) > 0) {
      if (useNextDay) {
        date = DateTimeHelper.addDays(date, 1);
      }
      return DateTimeHelper.startOfDay(date);
    }
    return date;
  }

  /**
   * @param date If date has an end time of 00:00:00, change it to 23:59:59.999 of previous day. Return the original date, otherwise.
   */
  public static transformToNearlyMidnightIfNeeded(date: Date): Date {
    if (DateTimeHelper.isMidnight(date)) {
      return DateTimeHelper.endOfDay(DateTimeHelper.subDays(date, 1));
    }
    return date;
  }

  public static truncateTime(value: Date): string {
    value.setHours(0, 0, 0, 0);
    return DateTimeHelper.dateToString(value);
  }

  public static setSecondsAndMillisToZero(date: Date): Date {
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  public static utcDate(): Date {
    const date = new Date();
    const offset = date.getTimezoneOffset();
    return moment(date).add(offset, 'm').toDate();
  }

  public static dateToString(date: Date, format = 'yyyy-MM-DDTHH:mm:ss'): string {
    return `${moment(date).format(format)}Z`;
  }

  public static dateToISO8601String(date: Date, removeTimezone = false): string {
    if (date === null) {
      return null;
    }
    let str = DateTimeHelper.format(date, "yyyy-MM-dd'T'HH:mm:ssxxx");
    if (removeTimezone) {
      str = `${str.substring(0, 19)}+0000`;
    } else {
      // Reformat to ISO 8601 without colon in timezone offset
      const n = str.lastIndexOf(':');
      str = str.substring(0, n) + str.substring(n + 1);
    }
    return str;
  }

  public static formatSecondsToHoursAndMinutes(sumSeconds: number): string {
    if (!Number.isFinite(sumSeconds)) {
      return '--:--';
    }
    const s = Math.max(0, Math.floor(sumSeconds));
    const hours = Math.floor(DateTimeHelper.secondsToHours(s));
    const minutes = Math.floor((s % secondsInHour) / secondsInMinute);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  public static hoursToSeconds(hours: number): number {
    return hoursToSeconds(hours);
  }

  public static minutesToSeconds(minutes: number): number {
    return minutesToSeconds(minutes);
  }

  public static secondsToHours(seconds: number): number {
    return seconds / secondsInHour;
  }

  public static secondsToTimeOfDay(seconds: number): Date {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    return addSeconds(base, seconds);
  }

  public static timeOfDayToSeconds(time: Date): number {
    return (
      DateTimeHelper.hoursToSeconds(time.getHours()) +
      DateTimeHelper.minutesToSeconds(time.getMinutes()) +
      time.getSeconds()
    );
  }
}
